import { css } from '@emotion/react'
import { s3URL } from '@mathflat/shared/@common/utils/s3'
import { tJosa } from '@mathflat/shared/i18n/tJosa'
import clsx from 'clsx'
import { m } from 'framer-motion'
import { observer } from 'mobx-react'
import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { routeName } from '~/@common/constants'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import authService from '~/@common/services/auth.service'
import { checkOnGnbNavigate } from '~/@common/services/forceUpdate/checkOnGnbNavigate'
import modalService from '~/@common/services/modal.service'
import { switchParentChild } from '~/@common/services/parentSession.service'
import { commonRepo } from '~/@common/services/repo.service'
import { colors, typo } from '~/@common/styles'
import { Drawer } from '~/@common/ui/(Drawer)/BaseDrawer'
import { Dropdown } from '~/@common/ui/Dropdown'
import { DropdownItem } from '~/@common/ui/Dropdown'
import { Icon } from '~/@common/ui/Icon/Icon'
import { getEntrancePage } from '~/@common/utils/getEntrancePage'
import { mixpanelClient } from '~/@common/utils/trackings/mixpanel'

import { S } from '.'
import { ChildSwitchModal } from './ChildSwitchModal'
import { GuideVideoButton } from './GuideVideoButton'
import NotificationButton from './NotificationButton'
import OpinionModal from './OpinionModal'
import { QRScanButton } from './QRScanButton'

export const GNB = observer(() => {
  const { isMobile } = useStudentAppMediaQuery()
  const location = useLocation()
  const [isChildDrawerOpen, setIsChildDrawerOpen] = useState(false)
  // 권한 로드 전에는 내부 메뉴는 렌더하지 않는다.
  if (!commonRepo.isPermissionLoaded) {
    return <S.Header />
  }
  const { isParent, byFeature } = commonRepo.permission
  // NavLink 메뉴는 라우트 가드와 동일
  const isChallengeEnabled = byFeature.isRouteAllowed(routeName.student.challenge)
  const isWorkbookEnabled = byFeature.isRouteAllowed(routeName.student.studentWorkbook)
  const isWorksheetEnabled = byFeature.isRouteAllowed(routeName.student.studentWorksheet)
  const isLectureEnabled = byFeature.isRouteAllowed(routeName.student.lectureList)
  const isCasEnabled = byFeature.isRouteAllowed(routeName.student.cas)
  const isReportEnabled = byFeature.isRouteAllowed(routeName.parent.report)
  const isNoticeEnabled = byFeature.isRouteAllowed(routeName.parent.notice)
  // 비-라우트 버튼/UI 는 restriction 직접 조회
  const isQREnabled = byFeature.isAllowed('QR')
  const isNotificationEnabled = byFeature.isAllowed('NOTIFICATION_CENTER')
  // 자녀 선택은 자녀가 2명 이상인 학부모일 때만 노출
  const isChildrenListEnabled =
    byFeature.isAllowed('CHILDREN_LIST') && commonRepo.parent.childStudents.length > 1
  const isFeedbackEnabled = byFeature.isAllowed('SEND_FEEDBACK')
  const isGuideVideoEnabled = byFeature.isAllowed('USER_GUIDE')
  return (
    <S.Header>
      {isMobile ? (
        <>
          <nav className={clsx('gnb__nav-top')}>
            <NavLink to={getEntrancePage(authService.userType)} className="gnb__nav-logo">
              <img
                src={commonRepo.gnbLogoUrl}
                alt={`${tJosa('학원')} 로고`}
                height={26}
                className="gnb__nav-logo-image"
              />
            </NavLink>
            <div className="gnb__nav-top-right">
              {isQREnabled && (
                <QRScanButton
                  qrButton={(isAppUpdated) => (
                    <div className="qr__button-wrapper">
                      <div className="qr__button">
                        <Icon name="icon_qr_scan" color={colors.gray.$600} size={26} />
                      </div>
                      {isAppUpdated && <div className="qr__icon-wrapper" />}
                    </div>
                  )}
                />
              )}
              {isNotificationEnabled && <NotificationButton size={32} iconSize={19} />}
            </div>
          </nav>
          <nav className={clsx('gnb__nav', 'gnb__nav-bottom')} onClick={checkOnGnbNavigate}>
            <NavLink
              to={getEntrancePage(authService.userType)}
              className={({ isActive }) => clsx('gnb__nav-link-mobile', isActive && 'active')}
              end
            >
              {({ isActive }) => (
                <div className="title-container">
                  학습 홈
                  {isActive && (
                    <m.div
                      className="underline"
                      layoutId="underline"
                      transition={{ layout: { duration: 0.2 } }}
                    />
                  )}
                </div>
              )}
            </NavLink>
            {isChallengeEnabled && (
              <NavLink
                to={routeName.student.challenge}
                className={({ isActive }) => clsx('gnb__nav-link-mobile', isActive && 'active')}
              >
                {({ isActive }) => (
                  <div className="title-container">
                    챌린지
                    {isActive && (
                      <m.div
                        className="underline"
                        layoutId="underline"
                        transition={{ layout: { duration: 0.2 } }}
                      />
                    )}
                  </div>
                )}
              </NavLink>
            )}
            {isWorkbookEnabled && (
              <NavLink
                to={routeName.student.studentWorkbook}
                className={({ isActive }) => clsx('gnb__nav-link-mobile', isActive && 'active')}
              >
                {({ isActive }) => (
                  <div className="title-container">
                    교재
                    {isActive && (
                      <m.div
                        className="underline"
                        layoutId="underline"
                        transition={{ layout: { duration: 0.2 } }}
                      />
                    )}
                  </div>
                )}
              </NavLink>
            )}
            {isWorksheetEnabled && (
              <NavLink
                to={routeName.student.studentWorksheet}
                className={({ isActive }) => clsx('gnb__nav-link-mobile', isActive && 'active')}
              >
                {({ isActive }) => (
                  <div className="title-container">
                    학습지
                    {isActive && (
                      <m.div
                        className="underline"
                        layoutId="underline"
                        transition={{ layout: { duration: 0.2 } }}
                      />
                    )}
                  </div>
                )}
              </NavLink>
            )}
            {isLectureEnabled && (
              <NavLink
                to={routeName.student.lectureList}
                className={({ isActive }) => clsx('gnb__nav-link-mobile', isActive && 'active')}
              >
                {({ isActive }) => (
                  <div className="title-container">
                    {tJosa('강의')}
                    {isActive && (
                      <m.div
                        className="underline"
                        layoutId="underline"
                        transition={{ layout: { duration: 0.2 } }}
                      />
                    )}
                  </div>
                )}
              </NavLink>
            )}
            {isCasEnabled && (
              <NavLink
                to={routeName.student.cas}
                className={({ isActive }) => clsx('gnb__nav-link-mobile', isActive && 'active')}
              >
                {({ isActive }) => (
                  <div className="title-container">
                    {tJosa('반응형평가')}
                    {isActive && (
                      <m.div
                        className="underline"
                        layoutId="underline"
                        transition={{ layout: { duration: 0.2 } }}
                      />
                    )}
                  </div>
                )}
              </NavLink>
            )}
            {isReportEnabled && (
              <NavLink
                to={routeName.parent.report}
                className={({ isActive }) => clsx('gnb__nav-link-mobile', isActive && 'active')}
              >
                {({ isActive }) => (
                  <div className="title-container">
                    보고서
                    {isActive && (
                      <m.div
                        className="underline"
                        layoutId="underline"
                        transition={{ layout: { duration: 0.2 } }}
                      />
                    )}
                  </div>
                )}
              </NavLink>
            )}
            {isNoticeEnabled && (
              <NavLink
                to={routeName.parent.notice}
                className={({ isActive }) => clsx('gnb__nav-link-mobile', isActive && 'active')}
              >
                {({ isActive }) => (
                  <div className="title-container">
                    공지
                    {isActive && (
                      <m.div
                        className="underline"
                        layoutId="underline"
                        transition={{ layout: { duration: 0.2 } }}
                      />
                    )}
                  </div>
                )}
              </NavLink>
            )}
            <NavLink
              to={routeName.profile}
              className={({ isActive }) => clsx('gnb__nav-link-mobile', isActive && 'active')}
            >
              {({ isActive }) => (
                <div className="title-container">
                  더보기
                  {isActive && (
                    <m.div
                      className="underline"
                      layoutId="underline"
                      transition={{ layout: { duration: 0.2 } }}
                    />
                  )}
                </div>
              )}
            </NavLink>
            {isChildrenListEnabled && (
              <button
                className="gnb__nav-link-mobile gnb__child-switch-mobile"
                onClick={() => setIsChildDrawerOpen(true)}
              >
                {commonRepo.parent.activeChildName}
                <Icon name="icon_dropdown_m_arrow_down" size={20} color={colors.gray.$600} />
              </button>
            )}
          </nav>
        </>
      ) : (
        <nav className="gnb__nav" onClick={checkOnGnbNavigate}>
          <NavLink
            to={getEntrancePage(authService.userType)}
            className="gnb__nav-logo"
            onClick={() => mixpanelClient.track('GNB_로고_Click')}
          >
            <img
              src={commonRepo.gnbLogoUrl}
              alt="기관 로고"
              height={32}
              className="gnb__nav-logo-image"
            />
          </NavLink>
          <NavLink
            to={getEntrancePage(authService.userType)}
            className={({ isActive }) => clsx('gnb__nav-link', isActive && 'active')}
            end
            onClick={() => mixpanelClient.track('GNB_학습홈탭_Click')}
          >
            <Icon name="icon_home" color={colors.blue.$500} size={20} />
            학습 홈
          </NavLink>
          {isChallengeEnabled && (
            <NavLink
              to={routeName.student.challenge}
              className={({ isActive }) => clsx('gnb__nav-link', isActive && 'active')}
              onClick={() => mixpanelClient.track('GNB_챌린지탭_Click')}
            >
              <Icon name="icon_smile" size={20} />
              챌린지
            </NavLink>
          )}
          {isWorkbookEnabled && (
            <NavLink
              to={routeName.student.studentWorkbook}
              className={({ isActive }) => clsx('gnb__nav-link', isActive && 'active')}
              onClick={() => mixpanelClient.track('GNB_교재탭_Click')}
            >
              <Icon name="icon_book_opened" color={colors.blue.$500} size={20} />
              교재
            </NavLink>
          )}
          {isWorksheetEnabled && (
            <NavLink
              to={routeName.student.studentWorksheet}
              className={({ isActive }) => clsx('gnb__nav-link', isActive && 'active')}
              onClick={() => mixpanelClient.track('GNB_학습지탭_Click')}
            >
              <Icon name="icon_description_paper" color={colors.blue.$500} size={20} />
              학습지
            </NavLink>
          )}
          {isLectureEnabled && (
            <NavLink
              to={routeName.student.lectureList}
              className={({ isActive }) => clsx('gnb__nav-link', isActive && 'active')}
              onClick={() => mixpanelClient.track('GNB_강의탭_Click')}
            >
              <Icon name="icon_video" color={colors.blue.$500} size={20} />
              {tJosa('강의')}
            </NavLink>
          )}
          {isCasEnabled && (
            <NavLink
              to={routeName.student.cas}
              className={({ isActive }) => clsx('gnb__nav-link', isActive && 'active')}
            >
              <Icon name="icon_description_paper" color={colors.blue.$500} size={20} />
              {tJosa('반응형평가')}
            </NavLink>
          )}
          {isReportEnabled && (
            <NavLink
              to={routeName.parent.report}
              className={({ isActive }) => clsx('gnb__nav-link', isActive && 'active')}
            >
              <Icon name="icon_assignment" color={colors.blue.$500} size={20} />
              보고서
            </NavLink>
          )}
          {isNoticeEnabled && (
            <NavLink
              to={routeName.parent.notice}
              className={({ isActive }) => clsx('gnb__nav-link', isActive && 'active')}
            >
              <Icon name="icon_speaker" color={colors.blue.$500} size={20} />
              공지
            </NavLink>
          )}
        </nav>
      )}
      {!isMobile && (
        <div className="gnb__userService">
          {/* 학부모용: 자녀 선택 드롭다운 (자녀 2명 이상일 때만 노출) */}
          {isChildrenListEnabled && (
            <Dropdown
              value={commonRepo.parent.activeChildStudentId}
              onChange={switchParentChild}
              height={40}
              style={{ width: 140 }}
              listWidth={140}
              guide={
                <div
                  css={css`
                    background-color: var(--Colors-Gray-800);
                    color: var(--White);
                    width: 100%;
                    padding: 10px 12px;
                    ${typo.caption01};
                  `}
                >
                  자녀를 선택해주세요.
                </div>
              }
            >
              {commonRepo.parent.childStudents.map((child) => (
                <DropdownItem key={child.studentId} value={child.studentId}>
                  {child.name}
                </DropdownItem>
              ))}
            </Dropdown>
          )}

          {/* 학생 전용: 의견남기기 */}
          {isFeedbackEnabled && (
            <button
              className="gnb__userService-opinion"
              onClick={() => {
                modalService.openModal(<OpinionModal />, {
                  hasCloseButton: true,
                  modalName: 'opinion-modal',
                  modalStyle: {
                    padding: '0px',
                  },
                })
                mixpanelClient.track('GNB_의견남기기버튼_Click')
              }}
            >
              <Icon name="icon_heart" />
              <span>의견 남기기</span>
            </button>
          )}

          <NavLink
            to={routeName.profile}
            className={clsx(
              'gnb__userService-profile',
              isParent && 'gnb__userService-profile--parent',
              location.pathname === routeName.profile && 'active',
            )}
            onClick={() => mixpanelClient.track('GNB_내정보버튼_Click')}
          >
            <img
              src={s3URL.student(
                location.pathname === routeName.profile
                  ? 'images/img__profile-active.svg'
                  : 'images/img__profile.svg',
              )}
              alt="프로필"
            />
            <div className="name">
              {isParent ? (
                <span>내정보</span>
              ) : (
                <>
                  <span>{commonRepo.student?.name}&nbsp;</span>
                  <span>학생</span>
                </>
              )}
            </div>
          </NavLink>
          {isQREnabled && (
            <QRScanButton
              qrButton={(isAppUpdated) => (
                <div className="qr__button-wrapper">
                  <div className="qr__button">
                    <Icon name="icon_qr_scan" color={colors.gray.$600} size={26} />
                  </div>
                  {isAppUpdated && <div className="qr__icon-wrapper" />}
                </div>
              )}
              tooltip={
                <div className="qr__button-guide-wrapper">
                  <div className="qr__button-guide">
                    QR 코드를 찍어 채점화면으로
                    <br />
                    빠르게 이동해 보세요!
                  </div>
                  <Icon
                    name="icon_tooltip_arrow-right"
                    color={colors.white}
                    size={13}
                    style={{
                      position: 'relative',
                      top: '22px',
                      right: '3px',
                    }}
                  />
                </div>
              }
            />
          )}
          {isNotificationEnabled && <NotificationButton size={42} iconSize={26} />}
          {isGuideVideoEnabled && <GuideVideoButton />}
        </div>
      )}
      {/* 모바일 학부모: 자녀 선택 바텀시트 */}
      {isChildrenListEnabled && isMobile && (
        <Drawer
          isOpened={isChildDrawerOpen}
          placement="bottom"
          closeDrawer={() => setIsChildDrawerOpen(false)}
        >
          <Drawer.Content>
            <Drawer.Header hasBorderBottom={false}>다른 자녀 보기</Drawer.Header>
            <ChildSwitchModal onClose={() => setIsChildDrawerOpen(false)} />
          </Drawer.Content>
        </Drawer>
      )}
    </S.Header>
  )
})
